/**
 * Domain vocabulary shared by the API and the web client.
 * Keep this file free of runtime dependencies so both sides can import it directly.
 */

export const SystemRole = {
  SUPER_ADMIN: 'super_admin',
  ORG_ADMIN: 'org_admin',
  PROJECT_MANAGER: 'project_manager',
  TEAM_LEAD: 'team_lead',
  TEAM_MEMBER: 'team_member',
  CLIENT: 'client',
} as const;
export type SystemRole = (typeof SystemRole)[keyof typeof SystemRole];

export const ROLE_ORDER: SystemRole[] = [
  SystemRole.CLIENT,
  SystemRole.TEAM_MEMBER,
  SystemRole.TEAM_LEAD,
  SystemRole.PROJECT_MANAGER,
  SystemRole.ORG_ADMIN,
  SystemRole.SUPER_ADMIN,
];

/** True when `role` sits at or above `minimum` in the hierarchy. */
export function roleAtLeast(role: SystemRole, minimum: SystemRole): boolean {
  return ROLE_ORDER.indexOf(role) >= ROLE_ORDER.indexOf(minimum);
}

export const Permission = {
  ORG_MANAGE: 'org:manage',
  ORG_VIEW: 'org:view',
  MEMBER_MANAGE: 'member:manage',
  WORKSPACE_MANAGE: 'workspace:manage',
  PROJECT_CREATE: 'project:create',
  PROJECT_UPDATE: 'project:update',
  PROJECT_DELETE: 'project:delete',
  PROJECT_VIEW: 'project:view',
  TASK_CREATE: 'task:create',
  TASK_UPDATE: 'task:update',
  TASK_DELETE: 'task:delete',
  TASK_VIEW: 'task:view',
  TIME_LOG: 'time:log',
  TIME_APPROVE: 'time:approve',
  REPORT_VIEW: 'report:view',
  BUDGET_VIEW: 'budget:view',
  AUDIT_VIEW: 'audit:view',
} as const;
export type Permission = (typeof Permission)[keyof typeof Permission];

const MEMBER_PERMISSIONS: Permission[] = [
  Permission.ORG_VIEW,
  Permission.PROJECT_VIEW,
  Permission.TASK_VIEW,
  Permission.TASK_CREATE,
  Permission.TASK_UPDATE,
  Permission.TIME_LOG,
];

const LEAD_PERMISSIONS: Permission[] = [
  ...MEMBER_PERMISSIONS,
  Permission.TASK_DELETE,
  Permission.REPORT_VIEW,
];

const PM_PERMISSIONS: Permission[] = [
  ...LEAD_PERMISSIONS,
  Permission.PROJECT_CREATE,
  Permission.PROJECT_UPDATE,
  Permission.PROJECT_DELETE,
  Permission.TIME_APPROVE,
  Permission.BUDGET_VIEW,
];

const ORG_ADMIN_PERMISSIONS: Permission[] = [
  ...PM_PERMISSIONS,
  Permission.ORG_MANAGE,
  Permission.MEMBER_MANAGE,
  Permission.WORKSPACE_MANAGE,
  Permission.AUDIT_VIEW,
];

/** Baseline permission grants per role; org-defined custom roles layer on top of these. */
export const ROLE_PERMISSIONS: Record<SystemRole, Permission[]> = {
  [SystemRole.CLIENT]: [Permission.PROJECT_VIEW, Permission.TASK_VIEW],
  [SystemRole.TEAM_MEMBER]: MEMBER_PERMISSIONS,
  [SystemRole.TEAM_LEAD]: LEAD_PERMISSIONS,
  [SystemRole.PROJECT_MANAGER]: PM_PERMISSIONS,
  [SystemRole.ORG_ADMIN]: ORG_ADMIN_PERMISSIONS,
  [SystemRole.SUPER_ADMIN]: Object.values(Permission),
};

export const ProjectMethodology = {
  AGILE: 'agile',
  SCRUM: 'scrum',
  KANBAN: 'kanban',
  WATERFALL: 'waterfall',
  HYBRID: 'hybrid',
  MAINTENANCE: 'maintenance',
} as const;
export type ProjectMethodology = (typeof ProjectMethodology)[keyof typeof ProjectMethodology];

/**
 * What each delivery methodology actually offers.
 *
 * A Kanban board with an empty Sprints tab, or a Waterfall plan asking for
 * story points, is worse than not offering them at all — so the methodology
 * drives which views and fields a project exposes. This is the single source of
 * truth; the API and the UI both read it rather than each hard-coding rules.
 */
export interface MethodologyProfile {
  key: ProjectMethodology;
  label: string;
  description: string;
  /** Views in tab order; the first is the project's landing view. */
  views: Array<'board' | 'list' | 'timeline' | 'backlog' | 'sprints' | 'insights' | 'bugs'>;
  /** Iteration planning: sprints, velocity, burndown, story points. */
  usesSprints: boolean;
  /** Continuous flow: WIP limits and cycle time instead of iterations. */
  usesWipLimits: boolean;
  /** Estimation currency shown on cards and in planning. */
  estimateUnit: 'points' | 'hours';
  /** Phase/stage-gate planning with milestone approvals as the primary rhythm. */
  usesPhases: boolean;
  color: string;
}

export const METHODOLOGY_PROFILES: Record<ProjectMethodology, MethodologyProfile> = {
  agile: {
    key: 'agile',
    label: 'Agile',
    description: 'Iterative delivery with a prioritised backlog and light-weight iterations.',
    views: ['board', 'list', 'backlog', 'sprints', 'timeline', 'insights'],
    usesSprints: true,
    usesWipLimits: false,
    estimateUnit: 'points',
    usesPhases: false,
    color: '#5B7FFF',
  },
  scrum: {
    key: 'scrum',
    label: 'Scrum',
    description: 'Fixed-length sprints with commitment, velocity, burndown and retrospectives.',
    views: ['board', 'sprints', 'backlog', 'list', 'timeline', 'insights'],
    usesSprints: true,
    usesWipLimits: false,
    estimateUnit: 'points',
    usesPhases: false,
    color: '#12B76A',
  },
  kanban: {
    key: 'kanban',
    label: 'Kanban',
    description: 'Continuous flow with work-in-progress limits — no iterations.',
    views: ['board', 'list', 'timeline', 'insights'],
    usesSprints: false,
    usesWipLimits: true,
    estimateUnit: 'hours',
    usesPhases: false,
    color: '#F0A202',
  },
  waterfall: {
    key: 'waterfall',
    label: 'Waterfall',
    description: 'Sequential phases with milestone gates and dependency-driven scheduling.',
    views: ['timeline', 'list', 'board', 'insights'],
    usesSprints: false,
    usesWipLimits: false,
    estimateUnit: 'hours',
    usesPhases: true,
    color: '#9C6ADE',
  },
  maintenance: {
    key: 'maintenance',
    label: 'Maintenance & Support',
    description:
      'Ticket-driven support and AMC work: a continuous intake queue, SLA clocks and defect analytics rather than iterations.',
    // Bugs lead, because on a support engagement the defect queue *is* the plan.
    views: ['bugs', 'board', 'list', 'insights'],
    usesSprints: false,
    usesWipLimits: true,
    estimateUnit: 'hours',
    usesPhases: false,
    color: '#E4572E',
  },
  hybrid: {
    key: 'hybrid',
    label: 'Hybrid',
    description: 'Phased delivery with iterative execution inside each phase.',
    views: ['board', 'list', 'sprints', 'backlog', 'timeline', 'insights'],
    usesSprints: true,
    usesWipLimits: true,
    estimateUnit: 'hours',
    usesPhases: true,
    color: '#00A9C4',
  },
};

export const methodologyProfile = (methodology: ProjectMethodology): MethodologyProfile =>
  METHODOLOGY_PROFILES[methodology] ?? METHODOLOGY_PROFILES.agile;

export const ProjectStatus = {
  PLANNING: 'planning',
  ACTIVE: 'active',
  ON_HOLD: 'on_hold',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
} as const;
export type ProjectStatus = (typeof ProjectStatus)[keyof typeof ProjectStatus];

export const HealthStatus = {
  ON_TRACK: 'on_track',
  AT_RISK: 'at_risk',
  OFF_TRACK: 'off_track',
} as const;
export type HealthStatus = (typeof HealthStatus)[keyof typeof HealthStatus];

/**
 * Built-in task type keys. Projects may add, rename, recolour or remove types;
 * these are only the defaults a new project starts with. Never assume a task's
 * `type` is one of these — always resolve it against the project's `taskTypes`.
 */
export const TaskType = {
  TASK: 'task',
  STORY: 'story',
  BUG: 'bug',
  ISSUE: 'issue',
  ENHANCEMENT: 'enhancement',
  CHANGE_REQUEST: 'change_request',
  MILESTONE: 'milestone',
  EPIC: 'epic',
  SUBTASK: 'subtask',
} as const;
export type TaskType = (typeof TaskType)[keyof typeof TaskType];

/** Same contract as TaskType: defaults only, resolved per project at runtime. */
export const Priority = {
  URGENT: 'urgent',
  HIGH: 'high',
  NORMAL: 'normal',
  LOW: 'low',
} as const;
export type Priority = (typeof Priority)[keyof typeof Priority];

export interface TaskTypeDefinition {
  key: string;
  label: string;
  color: string;
  /** Icon name from the client's icon registry; unknown names fall back to a generic glyph. */
  icon: string;
  order: number;
  /** Type keys permitted as children. Empty means this type is a leaf. */
  allowedChildTypes: string[];
  /** Milestone-like types render on the timeline as a point rather than a bar. */
  isMilestone: boolean;
}

export interface PriorityDefinition {
  key: string;
  label: string;
  color: string;
  /** Higher sorts first. Also drives the escalation arrows in the UI. */
  weight: number;
}

export const DEFAULT_TASK_TYPES: TaskTypeDefinition[] = [
  {
    key: 'epic',
    label: 'Epic',
    color: '#9C6ADE',
    icon: 'epic',
    order: 0,
    allowedChildTypes: ['story', 'task', 'bug', 'enhancement', 'change_request'],
    isMilestone: false,
  },
  {
    key: 'story',
    label: 'Story',
    color: '#12B76A',
    icon: 'story',
    order: 1,
    allowedChildTypes: ['task', 'bug', 'subtask'],
    isMilestone: false,
  },
  {
    key: 'task',
    label: 'Task',
    color: '#5B7FFF',
    icon: 'task',
    order: 2,
    allowedChildTypes: ['subtask'],
    isMilestone: false,
  },
  {
    key: 'bug',
    label: 'Bug',
    color: '#E2445C',
    icon: 'bug',
    order: 3,
    allowedChildTypes: ['subtask'],
    isMilestone: false,
  },
  {
    key: 'issue',
    label: 'Issue',
    color: '#FF8A5B',
    icon: 'issue',
    order: 4,
    allowedChildTypes: ['subtask'],
    isMilestone: false,
  },
  {
    key: 'enhancement',
    label: 'Enhancement',
    color: '#00A9C4',
    icon: 'enhancement',
    order: 5,
    allowedChildTypes: ['subtask'],
    isMilestone: false,
  },
  {
    key: 'change_request',
    label: 'Change Request',
    color: '#F0A202',
    icon: 'change_request',
    order: 6,
    allowedChildTypes: ['task', 'subtask'],
    isMilestone: false,
  },
  {
    key: 'subtask',
    label: 'Subtask',
    color: '#8592A3',
    icon: 'subtask',
    order: 7,
    allowedChildTypes: [],
    isMilestone: false,
  },
  {
    key: 'milestone',
    label: 'Milestone',
    color: '#F0A202',
    icon: 'milestone',
    order: 8,
    allowedChildTypes: [],
    isMilestone: true,
  },
];

export const DEFAULT_PRIORITIES: PriorityDefinition[] = [
  { key: 'urgent', label: 'Urgent', color: '#E2445C', weight: 40 },
  { key: 'high', label: 'High', color: '#F0A202', weight: 30 },
  { key: 'normal', label: 'Normal', color: '#5B7FFF', weight: 20 },
  { key: 'low', label: 'Low', color: '#8592A3', weight: 10 },
];

/** Ceiling on nesting depth, independent of what the type rules allow. */
export const MAX_TASK_DEPTH = 8;

/**
 * One rung of the work breakdown. A level holds **several** types, so
 * "level 2 is a Story or a Feature" is expressible without inventing a type per
 * combination.
 */
export interface HierarchyLevel {
  name: string;
  /** Types that live at this level. */
  typeKeys: string[];
  /** Allow an item to contain another of its own level (e.g. Task under Task). */
  allowSameLevelNesting: boolean;
}

export const DEFAULT_HIERARCHY_LEVELS: HierarchyLevel[] = [
  { name: 'Initiative', typeKeys: ['epic'], allowSameLevelNesting: false },
  { name: 'Feature', typeKeys: ['story', 'change_request'], allowSameLevelNesting: false },
  {
    name: 'Work item',
    typeKeys: ['task', 'bug', 'issue', 'enhancement'],
    allowSameLevelNesting: false,
  },
  { name: 'Breakdown', typeKeys: ['subtask'], allowSameLevelNesting: false },
];

/**
 * Turns levels into the per-type child rules the task service enforces.
 *
 * A type at level N may contain every type at level N+1, plus its own level
 * when `allowSameLevelNesting` is set. Types not placed on any level are left
 * as leaves rather than being silently deleted — an unplaced type is usually an
 * oversight in the editor, not an instruction to forbid all nesting under it.
 */
export function deriveChildTypes(
  levels: HierarchyLevel[],
  allTypeKeys: string[],
): Record<string, string[]> {
  const rules: Record<string, string[]> = {};
  for (const key of allTypeKeys) rules[key] = [];

  levels.forEach((level, index) => {
    const next = levels[index + 1];
    const children = [
      ...(next?.typeKeys ?? []),
      ...(level.allowSameLevelNesting ? level.typeKeys : []),
    ];
    for (const key of level.typeKeys) {
      // A type listed on two levels keeps the union of both sets.
      rules[key] = [...new Set([...(rules[key] ?? []), ...children])];
    }
  });

  return rules;
}

/** Fixed lifecycle buckets every custom status must map to, so reports stay comparable. */
export const StatusCategory = {
  TODO: 'todo',
  IN_PROGRESS: 'in_progress',
  BLOCKED: 'blocked',
  DONE: 'done',
} as const;
export type StatusCategory = (typeof StatusCategory)[keyof typeof StatusCategory];

/**
 * What someone does on a project, as opposed to what they may do to it.
 *
 * `ProjectMember.access` is permission — manager, lead, member, viewer. This is
 * the delivery role, which is what a status report, a handover or a resourcing
 * conversation actually asks about: two people can both be "member" while one
 * is the tester and the other the architect, and someone's job title is what
 * they are in the company rather than what they are on this engagement.
 *
 * Suggestions, not a closed list. The field stores whatever is typed into it,
 * because every engagement invents a role sooner or later and a dropdown that
 * cannot hold it would just be left blank.
 */
export const PROJECT_ROLE_SUGGESTIONS = [
  'Project Manager',
  'Scrum Master',
  'Business Analyst',
  'Solution Architect',
  'Tech Lead',
  'Developer',
  'Frontend Developer',
  'Backend Developer',
  'Mobile Developer',
  'QA Engineer',
  'Test Lead',
  'UI/UX Designer',
  'DevOps Engineer',
  'Data Engineer',
  'Support Engineer',
  'Product Owner',
  'Client Stakeholder',
];

export interface WorkflowStatus {
  key: string;
  label: string;
  color: string;
  category: StatusCategory;
  order: number;
  /** Kanban work-in-progress limit; 0 means unlimited. Advisory, not enforced. */
  wipLimit?: number;
}

export const DEFAULT_TASK_STATUSES: WorkflowStatus[] = [
  { key: 'backlog', label: 'Backlog', color: '#8592A3', category: StatusCategory.TODO, order: 0 },
  { key: 'todo', label: 'To Do', color: '#5B7FFF', category: StatusCategory.TODO, order: 1 },
  { key: 'in_progress', label: 'In Progress', color: '#F0A202', category: StatusCategory.IN_PROGRESS, order: 2 },
  { key: 'in_review', label: 'In Review', color: '#9C6ADE', category: StatusCategory.IN_PROGRESS, order: 3 },
  { key: 'blocked', label: 'Blocked', color: '#E2445C', category: StatusCategory.BLOCKED, order: 4 },
  { key: 'done', label: 'Done', color: '#12B76A', category: StatusCategory.DONE, order: 5 },
];

/**
 * Defect fields. Kept as first-class task fields rather than custom fields
 * because bug metrics (severity mix, ageing, reopen rate) have to be
 * comparable across every project regardless of its configuration.
 */
export const BugSeverity = {
  CRITICAL: 'critical',
  MAJOR: 'major',
  MINOR: 'minor',
  TRIVIAL: 'trivial',
} as const;
export type BugSeverity = (typeof BugSeverity)[keyof typeof BugSeverity];

export const BUG_SEVERITY_META: Record<
  BugSeverity,
  { label: string; color: string; weight: number; description: string }
> = {
  critical: {
    label: 'Critical',
    color: '#B42318',
    weight: 40,
    description: 'Production down or data loss — no workaround',
  },
  major: {
    label: 'Major',
    color: '#E2445C',
    weight: 30,
    description: 'Core function broken, workaround is painful',
  },
  minor: {
    label: 'Minor',
    color: '#F0A202',
    weight: 20,
    description: 'Limited impact with an acceptable workaround',
  },
  trivial: {
    label: 'Trivial',
    color: '#8592A3',
    weight: 10,
    description: 'Cosmetic or edge case',
  },
};

export const BugEnvironment = {
  PRODUCTION: 'production',
  STAGING: 'staging',
  UAT: 'uat',
  DEVELOPMENT: 'development',
} as const;
export type BugEnvironment = (typeof BugEnvironment)[keyof typeof BugEnvironment];

export const BugResolution = {
  FIXED: 'fixed',
  WONT_FIX: 'wont_fix',
  DUPLICATE: 'duplicate',
  CANNOT_REPRODUCE: 'cannot_reproduce',
  BY_DESIGN: 'by_design',
} as const;
export type BugResolution = (typeof BugResolution)[keyof typeof BugResolution];

/** Task types treated as defects for tracking and metrics. */
export const DEFECT_TYPE_KEYS = ['bug', 'issue'];

/**
 * How one piece of work relates to another.
 *
 * Every relation is stored on both tasks, as itself on one side and as its
 * inverse on the other — so "A blocks B" and "B is blocked by A" are one fact
 * with two readings, and neither side can be seen without the other. That is
 * why the set comes in pairs, and why `relates_to` is its own inverse.
 */
export const DependencyType = {
  BLOCKS: 'blocks',
  BLOCKED_BY: 'blocked_by',
  DUPLICATES: 'duplicates',
  DUPLICATED_BY: 'duplicated_by',
  CLONES: 'clones',
  CLONED_BY: 'cloned_by',
  CAUSES: 'causes',
  CAUSED_BY: 'caused_by',
  IMPLEMENTS: 'implements',
  IMPLEMENTED_BY: 'implemented_by',
  RELATES_TO: 'relates_to',
} as const;
export type DependencyType = (typeof DependencyType)[keyof typeof DependencyType];

/**
 * The words each relation is read with, and the relation stored on the far end.
 *
 * The label is what someone picks from and what the section is headed with, so
 * it reads as a sentence about the task in front of them: "is blocked by
 * PLAT-12". `inverse` is what the other task stores, and the two are checked
 * against each other by `LINK_TYPES` below — a pair that disagreed would write
 * a relation nobody could see from the other side.
 */
export const LINK_TYPE_META: Record<
  DependencyType,
  { label: string; inverse: DependencyType }
> = {
  [DependencyType.BLOCKS]: { label: 'blocks', inverse: DependencyType.BLOCKED_BY },
  [DependencyType.BLOCKED_BY]: { label: 'is blocked by', inverse: DependencyType.BLOCKS },
  [DependencyType.DUPLICATES]: { label: 'duplicates', inverse: DependencyType.DUPLICATED_BY },
  [DependencyType.DUPLICATED_BY]: {
    label: 'is duplicated by',
    inverse: DependencyType.DUPLICATES,
  },
  [DependencyType.CLONES]: { label: 'clones', inverse: DependencyType.CLONED_BY },
  [DependencyType.CLONED_BY]: { label: 'is cloned by', inverse: DependencyType.CLONES },
  [DependencyType.CAUSES]: { label: 'causes', inverse: DependencyType.CAUSED_BY },
  [DependencyType.CAUSED_BY]: { label: 'is caused by', inverse: DependencyType.CAUSES },
  [DependencyType.IMPLEMENTS]: { label: 'implements', inverse: DependencyType.IMPLEMENTED_BY },
  [DependencyType.IMPLEMENTED_BY]: {
    label: 'is implemented by',
    inverse: DependencyType.IMPLEMENTS,
  },
  // Symmetric: the only relation that reads the same from either end.
  [DependencyType.RELATES_TO]: { label: 'relates to', inverse: DependencyType.RELATES_TO },
};

/** Every relation, in the order a picker should offer them. */
export const LINK_TYPES = Object.values(DependencyType);

/** The relation stored on the other task. */
export const inverseLinkType = (type: DependencyType): DependencyType =>
  LINK_TYPE_META[type]?.inverse ?? DependencyType.RELATES_TO;

/** True when `field` should be shown for a task of `typeKey`. */
export const fieldAppliesTo = (
  field: { appliesToTypes?: string[] },
  typeKey: string,
): boolean => !field.appliesToTypes?.length || field.appliesToTypes.includes(typeKey);

export const CustomFieldType = {
  TEXT: 'text',
  NUMBER: 'number',
  DATE: 'date',
  SELECT: 'select',
  MULTI_SELECT: 'multi_select',
  CHECKBOX: 'checkbox',
  USER: 'user',
  URL: 'url',
  CURRENCY: 'currency',
} as const;
export type CustomFieldType = (typeof CustomFieldType)[keyof typeof CustomFieldType];

export const EmploymentType = {
  FULL_TIME: 'full_time',
  PART_TIME: 'part_time',
  CONTRACTOR: 'contractor',
  FREELANCER: 'freelancer',
  INTERN: 'intern',
} as const;
export type EmploymentType = (typeof EmploymentType)[keyof typeof EmploymentType];

export const EMPLOYMENT_TYPE_LABELS: Record<EmploymentType, string> = {
  [EmploymentType.FULL_TIME]: 'Full-time',
  [EmploymentType.PART_TIME]: 'Part-time',
  [EmploymentType.CONTRACTOR]: 'Contractor',
  [EmploymentType.FREELANCER]: 'Freelancer',
  [EmploymentType.INTERN]: 'Intern',
};

/**
 * External types are paid against a contract rather than payroll, so their
 * timesheets are the ones that gate invoicing and need approval before billing.
 */
export const EXTERNAL_EMPLOYMENT_TYPES: EmploymentType[] = [
  EmploymentType.CONTRACTOR,
  EmploymentType.FREELANCER,
];

export const isExternalResource = (type: EmploymentType): boolean =>
  EXTERNAL_EMPLOYMENT_TYPES.includes(type);

/** Default weekly capacity by employment type, used when a user has none set. */
export const DEFAULT_CAPACITY_HOURS: Record<EmploymentType, number> = {
  [EmploymentType.FULL_TIME]: 40,
  [EmploymentType.PART_TIME]: 20,
  [EmploymentType.CONTRACTOR]: 40,
  [EmploymentType.FREELANCER]: 20,
  [EmploymentType.INTERN]: 30,
};

/** One column in the configurable task table. */
export interface TaskColumnPreference {
  key: string;
  visible: boolean;
  width: number;
  order: number;
}

export const TASK_COLUMNS: Array<{
  key: string;
  label: string;
  defaultWidth: number;
  defaultVisible: boolean;
  /** The name column carries the tree control and cannot be hidden or reordered. */
  locked?: boolean;
  align?: 'left' | 'right' | 'center';
}> = [
  { key: 'name', label: 'Task', defaultWidth: 380, defaultVisible: true, locked: true },
  { key: 'status', label: 'Status', defaultWidth: 140, defaultVisible: true },
  { key: 'assignees', label: 'Assignees', defaultWidth: 130, defaultVisible: true },
  { key: 'priority', label: 'Priority', defaultWidth: 110, defaultVisible: true },
  { key: 'dueDate', label: 'Due', defaultWidth: 120, defaultVisible: true },
  { key: 'effort', label: 'Effort', defaultWidth: 150, defaultVisible: true },
  // Estimate and logged hours as their own sortable, roll-up-aware columns —
  // the combined Effort bar reads well but cannot be scanned down a column.
  { key: 'estimate', label: 'Est. hrs', defaultWidth: 100, defaultVisible: true, align: 'right' },
  { key: 'logged', label: 'Logged', defaultWidth: 100, defaultVisible: false, align: 'right' },
  { key: 'remaining', label: 'Remaining', defaultWidth: 110, defaultVisible: false, align: 'right' },
  { key: 'type', label: 'Type', defaultWidth: 120, defaultVisible: false },
  { key: 'sprint', label: 'Sprint', defaultWidth: 140, defaultVisible: false },
  // Phase sits beside sprint: it answers on a stage-gated project exactly what
  // sprint answers on an iterative one, so the two are never both meaningful.
  { key: 'phase', label: 'Phase', defaultWidth: 150, defaultVisible: false },
  { key: 'storyPoints', label: 'Points', defaultWidth: 90, defaultVisible: false, align: 'right' },
  { key: 'startDate', label: 'Start', defaultWidth: 120, defaultVisible: false },
  { key: 'tags', label: 'Tags', defaultWidth: 160, defaultVisible: false },
  { key: 'owner', label: 'Owner', defaultWidth: 130, defaultVisible: false },
  { key: 'created', label: 'Created', defaultWidth: 120, defaultVisible: false },
  { key: 'updated', label: 'Updated', defaultWidth: 120, defaultVisible: false },
];

export const defaultColumnPreferences = (): TaskColumnPreference[] =>
  TASK_COLUMNS.map((column, index) => ({
    key: column.key,
    visible: column.defaultVisible,
    width: column.defaultWidth,
    order: index,
  }));

/**
 * Default defect workflow.
 *
 * Separate from the task workflow on purpose: a defect moves through triage and
 * verification steps that make no sense on a feature, and a shared workflow
 * forces one of the two to carry states it never uses. Projects may edit these
 * freely; `category` is what keeps reporting comparable across projects.
 */
export const DEFAULT_BUG_STATUSES: WorkflowStatus[] = [
  { key: 'new', label: 'New', color: '#8592A3', category: 'todo', order: 0, wipLimit: 0 },
  { key: 'triaged', label: 'Triaged', color: '#F0A202', category: 'todo', order: 1, wipLimit: 0 },
  { key: 'in_progress', label: 'In Progress', color: '#5B7FFF', category: 'in_progress', order: 2, wipLimit: 0 },
  { key: 'ready_for_qa', label: 'Ready for QA', color: '#00A9C4', category: 'in_progress', order: 3, wipLimit: 0 },
  { key: 'verifying', label: 'Verifying', color: '#9C6ADE', category: 'in_progress', order: 4, wipLimit: 0 },
  { key: 'closed', label: 'Closed', color: '#12B76A', category: 'done', order: 5, wipLimit: 0 },
  // Rejected counts as done because it has left the active queue. Whether the
  // defect was actually fixed is carried by `resolution`, not by the category,
  // so ageing and open-defect counts stay honest either way.
  { key: 'rejected', label: 'Rejected', color: '#B42318', category: 'done', order: 6, wipLimit: 0 },
];

/**
 * Default defect types. A task whose `type` is one of the project's configured
 * bug types is treated as a defect: it resolves its status against
 * `bugStatuses`, its custom fields against the bug field set, and it appears in
 * the Bugs tab and the defect analytics.
 */
export const DEFAULT_BUG_TYPES: TaskTypeDefinition[] = [
  { key: 'bug', label: 'Bug', color: '#B42318', icon: 'bug', order: 0, allowedChildTypes: [], isMilestone: false },
  { key: 'defect', label: 'Defect', color: '#D92D20', icon: 'bug', order: 1, allowedChildTypes: [], isMilestone: false },
  { key: 'incident', label: 'Incident', color: '#E4572E', icon: 'alert', order: 2, allowedChildTypes: [], isMilestone: false },
  { key: 'regression', label: 'Regression', color: '#9C6ADE', icon: 'undo', order: 3, allowedChildTypes: [], isMilestone: false },
  { key: 'vulnerability', label: 'Vulnerability', color: '#7A0C2E', icon: 'shield', order: 4, allowedChildTypes: [], isMilestone: false },
];

/**
 * Default environments a defect can be found in.
 *
 * Configurable per project because "environment" is site-specific: a team with
 * Prod / Pre-prod / Sandbox and one with Dev / QA / UAT / Prod are describing
 * their own deployment pipeline, and a fixed list forces one of them to lie.
 */
export interface ProjectOption {
  key: string;
  label: string;
  color: string;
  order: number;
}

export const DEFAULT_BUG_ENVIRONMENTS: ProjectOption[] = [
  { key: 'production', label: 'Production', color: '#B42318', order: 0 },
  { key: 'staging', label: 'Staging', color: '#F0A202', order: 1 },
  { key: 'uat', label: 'UAT', color: '#9C6ADE', order: 2 },
  { key: 'development', label: 'Development', color: '#5B7FFF', order: 3 },
];

/** Keys of the built-in bug types, used to decide the default bug type set. */
export const DEFAULT_BUG_TYPE_KEYS: string[] = DEFAULT_BUG_TYPES.map((type) => type.key);

/**
 * How a defect relates to a task.
 *
 * Kept explicit rather than a single untyped link list: "this bug blocks that
 * story" and "this task is the fix for that bug" are different facts, and a
 * report that cannot tell them apart is not worth running.
 */
export const BugLinkType = {
  /** The defect was found while testing this task's output. */
  FOUND_IN: 'found_in',
  /** This task is the fix being carried out for the defect. */
  FIXED_BY: 'fixed_by',
  /** The defect blocks the task from completing. */
  BLOCKS: 'blocks',
  /** Loose association with no delivery consequence. */
  RELATED: 'related',
} as const;
export type BugLinkType = (typeof BugLinkType)[keyof typeof BugLinkType];

export const BUG_LINK_LABELS: Record<BugLinkType, { forward: string; reverse: string }> = {
  found_in: { forward: 'Found in', reverse: 'Defects found' },
  fixed_by: { forward: 'Fixed by', reverse: 'Fixes' },
  blocks: { forward: 'Blocks', reverse: 'Blocked by' },
  related: { forward: 'Related to', reverse: 'Related to' },
};

/**
 * Stage-gate lifecycle for phased delivery.
 *
 * A phase is not a status on a task — it is a container of work with an
 * approval boundary at its end. That boundary is the whole point of phased
 * delivery: nobody starts building until the design is signed off, and "signed
 * off" has to be a recorded decision by a named person rather than a shared
 * assumption.
 */
export const PhaseStatus = {
  /** Planned but not begun. */
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  /** Work finished, awaiting the gate decision. */
  IN_REVIEW: 'in_review',
  /** Gate passed — the next phase may start. */
  APPROVED: 'approved',
  /** Gate failed — sent back, and the phase reopens. */
  REJECTED: 'rejected',
  /** Deliberately not being done on this project. */
  SKIPPED: 'skipped',
} as const;
export type PhaseStatus = (typeof PhaseStatus)[keyof typeof PhaseStatus];

export const PHASE_STATUS_META: Record<
  PhaseStatus,
  { label: string; color: string; description: string }
> = {
  not_started: { label: 'Not started', color: '#8592A3', description: 'Waiting on the phase before it' },
  in_progress: { label: 'In progress', color: '#5B7FFF', description: 'Work is underway' },
  in_review: { label: 'In review', color: '#F0A202', description: 'Submitted for the gate decision' },
  approved: { label: 'Approved', color: '#12B76A', description: 'Gate passed' },
  rejected: { label: 'Rejected', color: '#B42318', description: 'Sent back for rework' },
  skipped: { label: 'Skipped', color: '#8592A3', description: 'Not applicable to this project' },
};

/** Phase statuses that count as finished for the purpose of opening the next one. */
export const PHASE_CLEARED: PhaseStatus[] = ['approved', 'skipped'];

export interface PhaseDefinition {
  key: string;
  name: string;
  description?: string;
  order: number;
  /** When false the phase closes without a sign-off step. */
  requiresApproval: boolean;
  /** Blocks starting until every earlier phase is cleared. */
  blockedByPrevious: boolean;
  color: string;
}

/**
 * A conventional delivery breakdown. Projects rename, reorder, add and remove
 * these freely — they are only what a new phased project starts with.
 */
export const DEFAULT_PHASES: PhaseDefinition[] = [
  { key: 'initiation', name: 'Initiation', order: 0, requiresApproval: true, blockedByPrevious: false, color: '#8592A3', description: 'Charter, scope and stakeholders agreed' },
  { key: 'requirements', name: 'Requirements', order: 1, requiresApproval: true, blockedByPrevious: true, color: '#5B7FFF', description: 'Signed-off functional and non-functional requirements' },
  { key: 'design', name: 'Design', order: 2, requiresApproval: true, blockedByPrevious: true, color: '#9C6ADE', description: 'Architecture and interface design approved' },
  { key: 'build', name: 'Build', order: 3, requiresApproval: true, blockedByPrevious: true, color: '#00A9C4', description: 'Implementation against the approved design' },
  { key: 'testing', name: 'Testing', order: 4, requiresApproval: true, blockedByPrevious: true, color: '#F0A202', description: 'System, integration and acceptance testing' },
  { key: 'uat', name: 'UAT', order: 5, requiresApproval: true, blockedByPrevious: true, color: '#E4572E', description: 'Client acceptance against the agreed criteria' },
  { key: 'deployment', name: 'Deployment', order: 6, requiresApproval: true, blockedByPrevious: true, color: '#12B76A', description: 'Release to production and handover' },
  { key: 'closure', name: 'Closure', order: 7, requiresApproval: false, blockedByPrevious: true, color: '#4C9F70', description: 'Retrospective, documentation and sign-off' },
];

/**
 * The report catalogue.
 *
 * Declared once and read by both sides: the API uses it to authorise a request
 * and the web app to decide what to offer. Two lists would drift, and the way
 * that drift shows up is a report visible in the menu that returns 403 — or
 * worse, one that is authorised but never offered, so nobody knows it exists.
 *
 * `requires` is an AND: a report appears only when the caller holds every
 * permission on it. `scope` says what the report is about, which is what the
 * UI groups by.
 */
export const ReportScope = {
  DELIVERY: 'delivery',
  FINANCE: 'finance',
  PEOPLE: 'people',
  QUALITY: 'quality',
} as const;
export type ReportScope = (typeof ReportScope)[keyof typeof ReportScope];

export interface ReportDefinition {
  key: string;
  title: string;
  description: string;
  scope: ReportScope;
  requires: Permission[];
  /** Methodologies this report is meaningful for; empty means all of them. */
  methodologies: ProjectMethodology[];
  /** Whether the report takes a date range. */
  usesDateRange: boolean;
  /**
   * Dimensions this report can be split by, first being the default.
   *
   * Scoped to one project, a per-project row is a single line and tells you
   * nothing — the useful question there is who contributed what. Same report,
   * different grouping, rather than a second report that could disagree.
   */
  groupings?: Array<'project' | 'person' | 'client'>;
}

export const REPORTS: ReportDefinition[] = [
  {
    key: 'project-status',
    title: 'Project status',
    description:
      'Health, progress, budget burn and overdue work across every project — the standing portfolio review.',
    scope: ReportScope.DELIVERY,
    requires: [Permission.REPORT_VIEW],
    methodologies: [],
    usesDateRange: false,
  },
  {
    key: 'time-summary',
    title: 'Time summary',
    description:
      'Hours by project, person and client, split billable, awaiting approval and non-billable.',
    scope: ReportScope.FINANCE,
    requires: [Permission.REPORT_VIEW],
    methodologies: [],
    usesDateRange: true,
    groupings: ['project', 'person', 'client'],
  },
  {
    key: 'utilisation',
    title: 'Employee utilisation',
    description:
      'Logged hours against contracted capacity, with the billable share per person.',
    scope: ReportScope.PEOPLE,
    requires: [Permission.REPORT_VIEW],
    methodologies: [],
    usesDateRange: true,
  },
  {
    key: 'financials',
    title: 'Project financials',
    description:
      'Budget against cost and revenue, with margin — the view a finance review runs on.',
    scope: ReportScope.FINANCE,
    requires: [Permission.REPORT_VIEW, Permission.BUDGET_VIEW],
    methodologies: [],
    usesDateRange: true,
    groupings: ['project', 'person', 'client'],
  },
  {
    key: 'client-billing',
    title: 'Client billing',
    description:
      'Invoiceable value per client, with hours still waiting on a signature shown separately.',
    scope: ReportScope.FINANCE,
    requires: [Permission.REPORT_VIEW, Permission.BUDGET_VIEW],
    methodologies: [],
    usesDateRange: true,
  },
  {
    key: 'defects',
    title: 'Defect report',
    description:
      'Open defects by severity and age, reopen rate and where they concentrate.',
    scope: ReportScope.QUALITY,
    requires: [Permission.REPORT_VIEW],
    methodologies: [],
    usesDateRange: false,
  },
  {
    key: 'sla',
    title: 'SLA compliance',
    description: 'Response against target on support work, and what has breached.',
    scope: ReportScope.QUALITY,
    requires: [Permission.REPORT_VIEW],
    methodologies: [ProjectMethodology.MAINTENANCE],
    usesDateRange: false,
  },
  {
    key: 'velocity',
    title: 'Sprint velocity',
    description: 'Committed against completed points per sprint, and the running average.',
    scope: ReportScope.DELIVERY,
    requires: [Permission.REPORT_VIEW],
    methodologies: [
      ProjectMethodology.AGILE,
      ProjectMethodology.SCRUM,
      ProjectMethodology.HYBRID,
    ],
    usesDateRange: false,
  },
  {
    key: 'phase-gates',
    title: 'Phase gates',
    description: 'Where each phased programme stands, and which gates have been sent back.',
    scope: ReportScope.DELIVERY,
    requires: [Permission.REPORT_VIEW],
    methodologies: [ProjectMethodology.WATERFALL, ProjectMethodology.HYBRID],
    usesDateRange: false,
  },
  {
    key: 'workload',
    title: 'Resource workload',
    description:
      'Assigned open work per person against capacity — who is over-committed and who is free.',
    scope: ReportScope.PEOPLE,
    requires: [Permission.REPORT_VIEW],
    methodologies: [],
    usesDateRange: false,
  },
  {
    key: 'performance',
    title: 'Resource performance',
    description:
      'Delivered work per person: throughput, on-time rate, estimate accuracy and rework.',
    scope: ReportScope.PEOPLE,
    requires: [Permission.REPORT_VIEW],
    methodologies: [],
    usesDateRange: true,
  },
];

export const REPORT_SCOPE_LABELS: Record<ReportScope, string> = {
  delivery: 'Delivery',
  finance: 'Finance',
  people: 'People',
  quality: 'Quality',
};

/** The reports a caller may run, given their effective permissions. */
export const reportsFor = (permissions: string[]): ReportDefinition[] =>
  REPORTS.filter((report) => report.requires.every((needed) => permissions.includes(needed)));

export const SprintStatus = {
  PLANNED: 'planned',
  ACTIVE: 'active',
  COMPLETED: 'completed',
} as const;
export type SprintStatus = (typeof SprintStatus)[keyof typeof SprintStatus];

export const TimeEntryStatus = {
  DRAFT: 'draft',
  SUBMITTED: 'submitted',
  APPROVED: 'approved',
  REJECTED: 'rejected',
} as const;
export type TimeEntryStatus = (typeof TimeEntryStatus)[keyof typeof TimeEntryStatus];

export const NotificationType = {
  TASK_ASSIGNED: 'task_assigned',
  TASK_STATUS_CHANGED: 'task_status_changed',
  TASK_DUE_TODAY: 'task_due_today',
  TASK_OVERDUE: 'task_overdue',
  MENTIONED: 'mentioned',
  COMMENT_ADDED: 'comment_added',
  APPROVAL_PENDING: 'approval_pending',
  PROJECT_HEALTH_CHANGED: 'project_health_changed',
} as const;
export type NotificationType = (typeof NotificationType)[keyof typeof NotificationType];

/**
 * What each notification is, and whether it emails by default.
 *
 * `defaultEmail` is deliberately conservative. Everything appears in the in-app
 * inbox regardless; email is for the few events worth interrupting someone's
 * day over. A platform that emails on every status change trains people to
 * filter the sender, and then the one message that mattered is filtered too.
 */
export const NOTIFICATION_TYPE_META: Record<
  NotificationType,
  { label: string; description: string; defaultEmail: boolean }
> = {
  task_assigned: {
    label: 'Work assigned to me',
    description: 'Someone assigns a task, defect or backlog item to this person.',
    defaultEmail: true,
  },
  mentioned: {
    label: 'Mentioned in a comment',
    description: 'Someone writes @name in a comment.',
    defaultEmail: true,
  },
  approval_pending: {
    label: 'Timesheet awaiting my approval',
    description: 'Submitted time is waiting for this approver. Freelancer time cannot be billed until it clears.',
    defaultEmail: true,
  },
  task_overdue: {
    label: 'My work is overdue',
    description: 'A daily sweep finds assigned work past its due date.',
    defaultEmail: true,
  },
  task_due_today: {
    label: 'My work is due today',
    description: 'A daily sweep finds assigned work due before the day is out.',
    defaultEmail: false,
  },
  comment_added: {
    label: 'Comment on my work',
    description: 'Someone comments on an item this person is assigned to or follows.',
    defaultEmail: false,
  },
  task_status_changed: {
    label: 'Status changed',
    description: 'Work this person is assigned to or follows moves to another status.',
    defaultEmail: false,
  },
  project_health_changed: {
    label: 'Project health changed',
    description: 'A project moves between on track, at risk and off track.',
    defaultEmail: false,
  },
};

/** One switch per notification type, held on the organisation. */
export interface NotificationSetting {
  /** In-app delivery is never disabled — the inbox is the record of what happened. */
  email: boolean;
}

export const defaultNotificationSettings = (): Record<string, NotificationSetting> =>
  Object.fromEntries(
    Object.entries(NOTIFICATION_TYPE_META).map(([type, meta]) => [
      type,
      { email: meta.defaultEmail },
    ]),
  );

/** Routing keys for the RabbitMQ topic exchange that carries background work. */
export const JobRoutingKey = {
  NOTIFICATION_DISPATCH: 'notification.dispatch',
  ACTIVITY_RECORD: 'activity.record',
  AUDIT_RECORD: 'audit.record',
  TASK_RECURRENCE: 'task.recurrence',
  REPORT_GENERATE: 'report.generate',
  PROJECT_ROLLUP: 'project.rollup',
} as const;
export type JobRoutingKey = (typeof JobRoutingKey)[keyof typeof JobRoutingKey];

/**
 * Saved-view filtering.
 *
 * Operators are deliberately few and typed by field kind: a filter builder that
 * offers "greater than" on a status is worse than one that offers nothing,
 * because it invites a query that can never match.
 */
export const FilterOperator = {
  IS: 'is',
  IS_NOT: 'is_not',
  CONTAINS: 'contains',
  NOT_CONTAINS: 'not_contains',
  IS_EMPTY: 'is_empty',
  IS_NOT_EMPTY: 'is_not_empty',
  GT: 'gt',
  LT: 'lt',
  BETWEEN: 'between',
  BEFORE: 'before',
  AFTER: 'after',
  IN_NEXT_DAYS: 'in_next_days',
  IN_LAST_DAYS: 'in_last_days',
  IS_OVERDUE: 'is_overdue',
} as const;
export type FilterOperator = (typeof FilterOperator)[keyof typeof FilterOperator];

export type FilterFieldKind =
  | 'choice'
  | 'multi_choice'
  | 'user'
  | 'text'
  | 'number'
  | 'date'
  | 'boolean';

export const OPERATORS_BY_KIND: Record<FilterFieldKind, FilterOperator[]> = {
  choice: ['is', 'is_not', 'is_empty', 'is_not_empty'],
  multi_choice: ['contains', 'not_contains', 'is_empty', 'is_not_empty'],
  user: ['contains', 'not_contains', 'is_empty', 'is_not_empty'],
  text: ['contains', 'not_contains', 'is', 'is_empty', 'is_not_empty'],
  number: ['is', 'gt', 'lt', 'between', 'is_empty', 'is_not_empty'],
  date: ['before', 'after', 'between', 'in_next_days', 'in_last_days', 'is_overdue', 'is_empty', 'is_not_empty'],
  boolean: ['is'],
};

export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  is: 'is',
  is_not: 'is not',
  contains: 'includes any of',
  not_contains: 'excludes',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
  gt: 'greater than',
  lt: 'less than',
  between: 'between',
  before: 'before',
  after: 'after',
  in_next_days: 'within next (days)',
  in_last_days: 'within last (days)',
  is_overdue: 'is overdue',
};

/** Operators that take no value, so the builder hides the value control. */
export const VALUELESS_OPERATORS: FilterOperator[] = ['is_empty', 'is_not_empty', 'is_overdue'];

export interface ViewFilter {
  /** Standard field name, or `custom:<key>` / `bug:<field>` for the extras. */
  field: string;
  operator: FilterOperator;
  value?: unknown;
}

export interface ViewDefinition {
  /** Whether every filter must match, or any one of them. */
  match: 'all' | 'any';
  filters: ViewFilter[];
}

export const ViewVisibility = {
  PRIVATE: 'private',
  SHARED: 'shared',
} as const;
export type ViewVisibility = (typeof ViewVisibility)[keyof typeof ViewVisibility];

/** Fields every project has, offered by the filter builder before custom ones. */
export const STANDARD_FILTER_FIELDS: Array<{
  field: string;
  label: string;
  kind: FilterFieldKind;
  group: string;
}> = [
  { field: 'status', label: 'Status', kind: 'choice', group: 'Task' },
  { field: 'statusCategory', label: 'Status category', kind: 'choice', group: 'Task' },
  { field: 'type', label: 'Type', kind: 'choice', group: 'Task' },
  { field: 'priority', label: 'Priority', kind: 'choice', group: 'Task' },
  { field: 'title', label: 'Title', kind: 'text', group: 'Task' },
  { field: 'tags', label: 'Tags', kind: 'multi_choice', group: 'Task' },
  { field: 'assigneeIds', label: 'Assignees', kind: 'user', group: 'People' },
  { field: 'ownerId', label: 'Owner', kind: 'user', group: 'People' },
  { field: 'createdById', label: 'Created by', kind: 'user', group: 'People' },
  { field: 'dueDate', label: 'Due date', kind: 'date', group: 'Dates' },
  { field: 'startDate', label: 'Start date', kind: 'date', group: 'Dates' },
  { field: 'completedAt', label: 'Completed', kind: 'date', group: 'Dates' },
  { field: 'createdAt', label: 'Created', kind: 'date', group: 'Dates' },
  { field: 'updatedAt', label: 'Updated', kind: 'date', group: 'Dates' },
  { field: 'estimatedHours', label: 'Estimate (h)', kind: 'number', group: 'Effort' },
  { field: 'actualHours', label: 'Logged (h)', kind: 'number', group: 'Effort' },
  { field: 'storyPoints', label: 'Story points', kind: 'number', group: 'Effort' },
  { field: 'sprintId', label: 'Sprint', kind: 'choice', group: 'Planning' },
  { field: 'parentTaskId', label: 'Parent task', kind: 'choice', group: 'Planning' },
  { field: 'slaBreached', label: 'SLA breached', kind: 'boolean', group: 'Planning' },
  { field: 'bug:severity', label: 'Bug severity', kind: 'choice', group: 'Defect' },
  { field: 'bug:environment', label: 'Bug environment', kind: 'choice', group: 'Defect' },
  { field: 'bug:resolution', label: 'Bug resolution', kind: 'choice', group: 'Defect' },
  { field: 'bug:isRegression', label: 'Regression', kind: 'boolean', group: 'Defect' },
  { field: 'bug:reopenCount', label: 'Reopen count', kind: 'number', group: 'Defect' },
];

/** Maps a custom field's type onto the filter kind that fits it. */
export const CUSTOM_FIELD_KIND: Record<string, FilterFieldKind> = {
  text: 'text',
  url: 'text',
  number: 'number',
  currency: 'number',
  date: 'date',
  select: 'choice',
  multi_select: 'multi_choice',
  checkbox: 'boolean',
  user: 'user',
};

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

export interface AuthUser {
  id: string;
  organizationId: string;
  email: string;
  firstName: string;
  lastName: string;
  role: SystemRole;
  permissions: Permission[];
  avatarColor: string;
  /** Data URI or URL; unset falls back to the coloured initials. */
  avatarUrl?: string;
  jobTitle?: string;
  timezone?: string;
  employmentType?: EmploymentType;
  /** Free-form UI preferences (task column layouts, etc.). */
  preferences?: Record<string, unknown>;
  /** Where this person was last working; the client opens there at sign-in. */
  lastWorkspaceId?: string;
}
